import { useTranslation } from 'react-i18next'
import { useDialog, usePolicy } from '../hooks'
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { Generator } from '../types'
import { Edit } from '@mui/icons-material'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import GeneratorFieldForm from './GeneratorFieldForm'
import { ReactNode } from 'react'

export interface GeneratorFieldEditButtonProps extends Omit<
  IconButtonProps,
  'children' | 'onClick' | 'onChange'
> {
  generator: Generator
  field: string
  title?: string
  icon?: ReactNode
  tooltip?: ReactNode
  dialogProps?: Omit<DialogProps, 'open' | 'onClose' | 'children'>
  onGeneratorChange?: (generator: Generator) => void
}

export default function GeneratorFieldEditButton({
  generator,
  field,
  title,
  icon,
  tooltip,
  onGeneratorChange,
  dialogProps,
  ...props
}: GeneratorFieldEditButtonProps) {
  const { t } = useTranslation('generator')

  const dispatch = useDispatch()

  const { canEditGenerator } = usePolicy()

  const { open, openDialog, closeDialog } = useDialog()

  function afterSubmit(generator: Generator) {
    dispatch(createSuccessNotification(t('updateGeneratorSuccess')))
    onGeneratorChange?.(generator)
    closeDialog()
  }

  function afterError() {
    dispatch(createErrorNotification(t('updateGeneratorError')))
  }

  if (!canEditGenerator(generator)) {
    return null
  }

  return (
    <>
      <Tooltip title={tooltip ?? t('actions:edit')}>
        <IconButton {...props} onClick={openDialog}>
          {icon ?? <Edit color="action" />}
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth {...dialogProps}>
        <DialogTitle>{title ?? t('editGenerator')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <GeneratorFieldForm
              generator={generator}
              field={field}
              onCancel={closeDialog}
              afterSubmit={afterSubmit}
              afterError={afterError}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
